import { HOME_COPY, HOME_TILES } from '@/constants/home';
import { toAccountName } from '@/utils/accountName';
import { toLongDate } from '@/utils/date';
import {
  HOME_CHECK_FACETS,
  HOME_MEMBER_FACETS,
  hasHomeSource,
  toHomeTiles,
} from '@/utils/homeTiles';
import { toStaffDisplayName } from '@/utils/staffName';
import { useCounters } from '@/hooks/counters';
import { useCheckCounts } from '@/hooks/verification';
import { useLive } from '@/hooks/live';
import { useMemberCounts } from '@/hooks/members';
import { useStaffInterviews } from '@/hooks/interviews';
import { useStaffProfile } from '@/hooks/staff';
import { useStaffSession } from '@/hooks/auth';

import type { TPersonaScreen } from './types';

const usePersonaScreen = (): TPersonaScreen => {
  const { data: session } = useStaffSession();
  const { data: profile } = useStaffProfile();

  const persona = session?.subRole ?? null;
  const specs = persona ? HOME_TILES[persona] : [];

  const isOwnInterviews = hasHomeSource(specs, 'own-interviews');
  const isAllInterviews = hasHomeSource(specs, 'all-interviews');
  const interviewerID = isOwnInterviews ? profile?.userID : undefined;
  const isInterviewsWanted = isAllInterviews || (isOwnInterviews && interviewerID !== undefined);
  const isMembersWanted = hasHomeSource(specs, 'all-clients') || hasHomeSource(specs, 'all-singles');

  const { data: counters } = useCounters();
  const { totalUnread } = useLive();
  const { data: interviews } = useStaffInterviews({ interviewerID }, isInterviewsWanted);
  const checkCounts = useCheckCounts(HOME_CHECK_FACETS, hasHomeSource(specs, 'open-checks'));
  const memberCounts = useMemberCounts(HOME_MEMBER_FACETS, isMembersWanted);

  const fallbackName = toAccountName(session?.email ?? '');
  const displayName = profile ? toStaffDisplayName(profile.firstName, profile.lastName) : '';

  const tiles = toHomeTiles(specs, {
    counters,
    interviews: interviews?.rows,
    openChecks: checkCounts['open-checks'],
    allClients: memberCounts['all-clients'],
    allSingles: memberCounts['all-singles'],
    unreadMessages: totalUnread,
  });

  return {
    persona,
    name: displayName || fallbackName,
    subtitle: `${toLongDate(new Date())} · ${HOME_COPY.subtitle}`,
    tiles: persona ? tiles : null,
  };
};

export type {
  TPersonaScreen,
};

export {
  usePersonaScreen,
};
